import {
    ArrowPathIcon,
    BellAlertIcon,
    CheckCircleIcon,
    ClockIcon,
    DocumentPlusIcon,
    EnvelopeOpenIcon,
    ExclamationTriangleIcon,
    PaperAirplaneIcon,
} from "@heroicons/react/24/outline";
import type { SalesInvoiceTimelineEntry } from "./types/salesInvoice";
import { formatDateTimeDDMMYYYY } from "./utils/salesInvoicesFormat";

const TIMELINE_ICONS: Record<string, typeof PaperAirplaneIcon> = {
    created: DocumentPlusIcon,
    issued: CheckCircleIcon,
    sent: PaperAirplaneIcon,
    resent: ArrowPathIcon,
    reminder_sent: BellAlertIcon,
    delivered: EnvelopeOpenIcon,
    opened: EnvelopeOpenIcon,
    bounced: ExclamationTriangleIcon,
    marked_paid: CheckCircleIcon,
};

export function InvoiceTimeline({
    entries,
    isLoading,
}: {
    entries: SalesInvoiceTimelineEntry[] | null;
    isLoading: boolean;
}) {
    if (isLoading) {
        return <div className="h-24 animate-pulse rounded-lg bg-gray-100" />;
    }
    if (!entries || entries.length === 0) {
        return null;
    }
    return (
        <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Tijdlijn</h3>
            <ol>
                {entries.map((entry, index) => {
                    const Icon = TIMELINE_ICONS[entry.type] ?? ClockIcon;
                    const isProblem = entry.type === "bounced";
                    const isLast = index === entries.length - 1;
                    return (
                        <li key={`${entry.type}-${entry.occurred_at}-${index}`} className="relative flex gap-3 pb-5 last:pb-0">
                            {!isLast && (
                                <span
                                    aria-hidden="true"
                                    className="absolute left-3 top-6 h-full w-px bg-gray-200"
                                />
                            )}
                            <span
                                className={`flex h-6 w-6 flex-none items-center justify-center rounded-full ${
                                    isProblem ? "bg-red-500" : "bg-green-500"
                                }`}
                            >
                                <Icon className="h-3.5 w-3.5 text-white" />
                            </span>
                            <div className="min-w-0">
                                <p
                                    className={`text-sm ${
                                        isProblem ? "text-red-700 font-medium" : "text-gray-900 font-medium"
                                    }`}
                                >
                                    {entry.description}
                                </p>
                                <p className="text-xs text-gray-400">
                                    {entry.subtitle ?? formatDateTimeDDMMYYYY(entry.occurred_at)}
                                    {entry.failure_reason ? ` · ${entry.failure_reason}` : ""}
                                </p>
                            </div>
                        </li>
                    );
                })}
            </ol>
        </div>
    );
}
